/**
 * Utilitar pentru formatarea consistenta a datelor si orelor in timezone-ul Romaniei
 * Timezone: Europe/Bucharest (UTC+2 / UTC+3 in timpul verii)
 */

// Configurare pentru timezone-ul Romaniei
const TIMEZONE = 'Europe/Bucharest';
const LOCALE = 'ro-RO';

/**
 * Formateaza o data in timezone-ul Romaniei
 * @param date - Data de formatat (Date, string sau number)
 * @param options - Optiuni de formatare Intl.DateTimeFormat
 * @returns Data formatata ca string
 */
export const formatDate = (
  date: Date | string | number,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  }
): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return new Intl.DateTimeFormat(LOCALE, {
    ...options,
    timeZone: TIMEZONE
  }).format(dateObj);
};

/**
 * Returneaza data curenta in timezone-ul Romaniei
 * @returns Obiect Date
 */
export const getNow = (): Date => {
  return new Date();
};

/**
 * Formateaza o data pentru afisare in format romanesc standard
 * @param date - Data de formatat
 * @returns Data formatata (ex: 10.10.2025, 14:30)
 */
export const formatForDisplay = (date: Date | string | number): string => {
  if (!date) return 'Data indisponibila';
  
  try {
    const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
    
    if (isNaN(dateObj.getTime())) {
      console.warn('⚠️ Data invalida pentru formatare:', date);
      return 'Data invalida';
    }
    
    return formatDate(date, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('❌ Eroare la formatarea datei:', date, error);
    return 'Data indisponibila';
  }
};

/**
 * Formateaza o data pentru afisare in format romanesc fara ora
 * @param date - Data de formatat
 * @returns Data formatata (ex: 10.10.2025)
 */
export const formatDateOnly = (date: Date | string | number): string => {
  return formatDate(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Formateaza doar ora
 * @param date - Data de formatat
 * @returns Ora formatata (ex: 14:30)
 */
export const formatTimeOnly = (date: Date | string | number): string => {
  return formatDate(date, {
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formateaza o data in format lung romanesc
 * @param date - Data de formatat
 * @returns Data formatata (ex: vineri, 10 octombrie 2025, 14:30)
 */
export const formatLongDate = (date: Date | string | number): string => {
  return formatDate(date, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formateaza o data cu secunde
 * @param date - Data de formatat
 * @returns Data formatata cu secunde (ex: 10.10.2025, 14:30:45)
 */
export const formatWithSeconds = (date: Date | string | number): string => {
  return formatDate(date, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

/**
 * Verifica daca o data este in viitor
 * @param date - Data de verificat
 * @returns true daca data este in viitor
 */
export const isFuture = (date: Date | string | number): boolean => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return dateObj.getTime() > Date.now();
};

/**
 * Verifica daca o data este in trecut
 * @param date - Data de verificat
 * @returns true daca data este in trecut
 */
export const isPast = (date: Date | string | number): boolean => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return dateObj.getTime() < Date.now();
};

/**
 * Returneaza inceputul zilei pentru o data data
 * @param date - Data (implicit: azi)
 * @returns Inceputul zilei (00:00:00)
 */
export const startOfDay = (date: Date | string | number = new Date()): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const newDate = new Date(dateObj);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

/**
 * Returneaza sfarsitul zilei pentru o data data
 * @param date - Data (implicit: azi)
 * @returns Sfarsitul zilei (23:59:59)
 */
export const endOfDay = (date: Date | string | number = new Date()): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const newDate = new Date(dateObj);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

/**
 * Adauga zile la o data
 * @param date - Data initiala
 * @param days - Numarul de zile de adaugat (poate fi negativ)
 * @returns Noua data
 */
export const addDays = (date: Date | string | number, days: number): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const newDate = new Date(dateObj);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

/**
 * Adauga ore la o data
 * @param date - Data initiala
 * @param hours - Numarul de ore de adaugat (poate fi negativ)
 * @returns Noua data
 */
export const addHours = (date: Date | string | number, hours: number): Date => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const newDate = new Date(dateObj);
  newDate.setHours(newDate.getHours() + hours);
  return newDate;
};

/**
 * Calculeaza diferenta in zile intre doua date
 * @param date1 - Prima data
 * @param date2 - A doua data
 * @returns Diferenta in zile
 */
export const diffInDays = (date1: Date | string | number, date2: Date | string | number): number => {
  const dateObj1 = typeof date1 === 'string' || typeof date1 === 'number' ? new Date(date1) : date1;
  const dateObj2 = typeof date2 === 'string' || typeof date2 === 'number' ? new Date(date2) : date2;
  const diffTime = dateObj1.getTime() - dateObj2.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Calculeaza diferenta in ore intre doua date
 * @param date1 - Prima data
 * @param date2 - A doua data
 * @returns Diferenta in ore
 */
export const diffInHours = (date1: Date | string | number, date2: Date | string | number): number => {
  const dateObj1 = typeof date1 === 'string' || typeof date1 === 'number' ? new Date(date1) : date1;
  const dateObj2 = typeof date2 === 'string' || typeof date2 === 'number' ? new Date(date2) : date2;
  const diffTime = dateObj1.getTime() - dateObj2.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60));
};

/**
 * Formatare relativa (ex: "acum 5 minute", "in 2 ore")
 * @param date - Data de formatat
 * @returns String cu timpul relativ
 */
export const formatRelative = (date: Date | string | number): string => {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const now = new Date();
  const diffMs = dateObj.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (Math.abs(diffMins) < 1) {
    return 'acum';
  } else if (Math.abs(diffMins) < 60) {
    return diffMins > 0 ? `in ${diffMins} minute` : `acum ${Math.abs(diffMins)} minute`;
  } else if (Math.abs(diffHours) < 24) {
    return diffHours > 0 ? `in ${diffHours} ore` : `acum ${Math.abs(diffHours)} ore`;
  } else if (Math.abs(diffDays) < 7) {
    return diffDays > 0 ? `in ${diffDays} zile` : `acum ${Math.abs(diffDays)} zile`;
  } else {
    return formatDateOnly(dateObj);
  }
};

/**
 * Parseaza un string de data in format ISO
 * @param dateString - String-ul de data
 * @returns Obiect Date
 */
export const parseISODate = (dateString: string): Date => {
  return new Date(dateString);
};

/**
 * Verifica daca doua date sunt in aceeasi zi
 * @param date1 - Prima data
 * @param date2 - A doua data
 * @returns true daca sunt in aceeasi zi
 */
export const isSameDay = (date1: Date | string | number, date2: Date | string | number): boolean => {
  const d1 = typeof date1 === 'string' || typeof date1 === 'number' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' || typeof date2 === 'number' ? new Date(date2) : date2;
  return d1.getFullYear() === d2.getFullYear() &&
         d1.getMonth() === d2.getMonth() &&
         d1.getDate() === d2.getDate();
};

/**
 * Constante utile
 */
export const DATE_CONSTANTS = {
  TIMEZONE,
  LOCALE,
  FORMATS: {
    SHORT_DATE: 'dd.MM.yyyy',
    LONG_DATE: 'EEEE, d MMMM yyyy',
    SHORT_TIME: 'HH:mm',
    LONG_TIME: 'HH:mm:ss',
    DATE_TIME: 'dd.MM.yyyy HH:mm',
    ISO: 'yyyy-MM-dd\'T\'HH:mm:ss.SSSxxx'
  }
};

const dateFormatter = {
  formatDate,
  getNow,
  formatForDisplay,
  formatDateOnly,
  formatTimeOnly,
  formatLongDate,
  formatWithSeconds,
  isFuture,
  isPast,
  startOfDay,
  endOfDay,
  addDays,
  addHours,
  diffInDays,
  diffInHours,
  formatRelative,
  parseISODate,
  isSameDay,
  DATE_CONSTANTS
};

export default dateFormatter;

